import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import Swal from "sweetalert2";
import withRouter from "components/Common/withRouter";

import { Card, CardBody, Container } from "reactstrap";

import TableContainer from "components/Common/TableContainer";
import MetadataFilterToolbar from "components/Common/MetadataFilterToolbar";
import {
  submitCallManagerOrderInProgressFilterData,
  submitCallManagerOrderInProgressMarkEligible,
  updateCallDetailsFedexId,
} from "model/CallModel";
import { useCallExecutiveContext } from "libs/context/CallExecutiveContext";
import { usePermissionContext } from "libs/context/PermissionContext";
import AllCommonFilterModal from "pages/AllCommonFilter/all-common-filter";
import Loaders from "components/Common/Loaders";
import { toast } from "react-toastify";
import {
  DEFAULT_PAGE_SIZE,
  buildCallExecutiveTableColumnsFromMetadata,
  isCallExecutiveFedexIdAccessor,
  mapApiMetadataTableColumns,
  urlEndPoint,
} from "helpers/GeneralHelper";
import {
  getAsyncFilterLabelFieldKey,
  isAsyncFilterLabelFieldKey,
  isMetadataAsyncSelectFilterDefinition,
  mergeModalSubmitIntoListFilter,
} from "helpers/metadataFilterHelpers";
import { useMetadataDrivenFilters } from "hooks/useMetadataDrivenFilters";
import { useCallExecutiveAppliedFilter } from "hooks/useCallExecutiveAppliedFilter";
import { useCommonTableSettingsPageSize } from "hooks/useSettingsReadyTablePageSize";
import { resolveServerPageSizeMeta } from "components/Common/ServerListPagination";

const PAGE_NAME = "CallManagerOrderInProgress";

const extractList = (response) => {
  if (response == null) return [];
  const raw = response?.data;
  if (Array.isArray(raw)) return raw;
  if (raw && Array.isArray(raw.records)) return raw.records;
  if (raw && Array.isArray(raw.data)) return raw.data;
  return [];
};

const getPaginationEnvelope = (response) => {
  const raw = response?.data;
  if (raw && typeof raw === "object" && !Array.isArray(raw)) return raw;
  return {};
};

const buildPaginationMeta = (envelope, payload, recordsLength) => ({
  pageNumber: Number(envelope?.pageNumber) || Number(payload?.pageNumber) || 1,
  pageSize: resolveServerPageSizeMeta(
    envelope?.pageSize,
    payload?.pageSize,
    DEFAULT_PAGE_SIZE
  ),
  totalPages: Number(envelope?.totalPages) || 0,
  totalRecords: Number(envelope?.totalRecords) || recordsLength,
});

const mapRows = (list) =>
  (Array.isArray(list) ? list : []).map((item) => ({
    ...item,
    id: item?.id ?? item?.Id ?? item?._id,
  }));

const normalizeCallManagerFilterPayload = (raw = {}) => {
  const out = { ...raw };
  Object.keys(out).forEach((key) => {
    if (isAsyncFilterLabelFieldKey(key)) {
      delete out[key];
      return;
    }
    const v = out[key];
    if (v === "" || v === undefined) out[key] = null;
  });
  return out;
};

const OrderInProgress = () => {
  const {
    fetchCallManagerOrderInProgressContextData,
    callManagerOrderInProgressContextData,
  } = useCallExecutiveContext() || {};
  const { permissionData } = usePermissionContext() || {};
  const { pageSize: settingsPageSize, settingsReady } =
    useCommonTableSettingsPageSize(PAGE_NAME);
  const initialFilterFetchedRef = useRef(false);

  const orderInProgressActionPermissions = useMemo(
    () => ({
      canCallDetails: Boolean(
        permissionData?.CallCallDetailsAccess ||
          permissionData?.CallDetailsAccess ||
          permissionData?.CallMgrCallDetailsAccess
      ),
      canVideoInterview: Boolean(permissionData?.InterviewAccess),
      canMarkEligible: Boolean(permissionData?.CallMgrMarkEligibleAccess),
      canEditFedexId: Boolean(permissionData?.InterviewFedexDriverAccess),
    }),
    [permissionData]
  );

  const hasAnyOrderInProgressRowAction = useMemo(() => {
    const { canEditFedexId, ...rowActions } = orderInProgressActionPermissions;
    return Object.values(rowActions).some(Boolean);
  }, [orderInProgressActionPermissions]);

  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableMetaData, setTableMetadata] = useState();
  const [commonFilterModalOpen, setCommonFilterModalOpen] = useState(false);
  const [commonResetFilter, setCommonResetFilter] = useState(false);
  const [callManagerListFilter, setCallManagerListFilter] = useState({});
  const {
    initialLoadDoneRef,
    commitDraftAsApplied,
    resolveFilterBase,
    setAppliedFilter,
  } = useCallExecutiveAppliedFilter();
  const [paginationMeta, setPaginationMeta] = useState({
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    totalPages: 0,
    totalRecords: 0,
  });

  const {
    shouldShowTableHeaderFilterAndResetControls,
    shouldRenderMetadataToolbarFilterRow,
    activeModalFilterCount,
    modalFilterData: callManagerFilterModalData,
    metadataToolbarFilterDefinitions,
    metadataFiltersAll,
    usesMetadataDrivenFilterUi,
    buildBaselineEmptyListFilter,
    toolbarDropdownReactSelectOptionsByFieldName,
  } = useMetadataDrivenFilters(
    callManagerOrderInProgressContextData?.filters,
    callManagerListFilter,
    PAGE_NAME
  );

  useEffect(() => {
    const metadataFilters = callManagerOrderInProgressContextData?.filters;
    if (!Array.isArray(metadataFilters) || !metadataFilters.length) return;
    setCallManagerListFilter((previousFilterState) => {
      const mergedFilterState = { ...previousFilterState };
      let mergedNewKeysIntoState = false;
      metadataFilters.forEach((filterDefinitionRow) => {
        const fieldKey = filterDefinitionRow?.field;
        if (!fieldKey) return;
        if (!(fieldKey in mergedFilterState)) {
          mergedFilterState[fieldKey] =
            filterDefinitionRow?.type === "date" ? null : "";
          mergedNewKeysIntoState = true;
        }
        if (isMetadataAsyncSelectFilterDefinition(filterDefinitionRow)) {
          const asyncLabelFieldKey = getAsyncFilterLabelFieldKey(fieldKey);
          if (!(asyncLabelFieldKey in mergedFilterState)) {
            mergedFilterState[asyncLabelFieldKey] = "";
            mergedNewKeysIntoState = true;
          }
        }
      });
      return mergedNewKeysIntoState ? mergedFilterState : previousFilterState;
    });
  }, [callManagerOrderInProgressContextData?.filters]);

  const loadList = useCallback(
    async (overrides = {}, filterOverride) => {
      const filterBase = resolveFilterBase(filterOverride);
      setLoading(true);
      try {
        const payload = normalizeCallManagerFilterPayload({
          pageNumber: 1,
          pageSize: DEFAULT_PAGE_SIZE,
          ...filterBase,
          ...overrides,
        });
        const response = await submitCallManagerOrderInProgressFilterData(payload);
        const list = extractList(response);
        const records = Array.isArray(list) ? list : [];
        setTableData(mapRows(records));
        const envelope = getPaginationEnvelope(response);
        setPaginationMeta(buildPaginationMeta(envelope, payload, records.length));
      } finally {
        setLoading(false);
      }
    },
    [resolveFilterBase]
  );

  const loadListRef = useRef(loadList);
  loadListRef.current = loadList;

  useEffect(() => {
    fetchCallManagerOrderInProgressContextData?.();
  }, [fetchCallManagerOrderInProgressContextData]);

  useEffect(() => {
    if (!settingsReady || initialFilterFetchedRef.current) return;
    initialFilterFetchedRef.current = true;
    initialLoadDoneRef.current = true;
    const pageSize = Number(settingsPageSize) || DEFAULT_PAGE_SIZE;
    setPaginationMeta((prev) => ({ ...prev, pageSize }));
    setAppliedFilter({});
    loadListRef.current({ pageNumber: 1, pageSize }, {});
  }, [settingsReady, settingsPageSize, setAppliedFilter, initialLoadDoneRef]);

  useEffect(() => {
    const columnsFromMetadata = mapApiMetadataTableColumns(
      callManagerOrderInProgressContextData?.table?.columns
    );
    const mergedColumns =
      buildCallExecutiveTableColumnsFromMetadata(columnsFromMetadata);
    setTableMetadata(
      mergedColumns.length > 0 ? mergedColumns : columnsFromMetadata
    );
  }, [callManagerOrderInProgressContextData]);

  const handleFilterReset = useCallback(async () => {
    const emptyFilter = buildBaselineEmptyListFilter();
    setCallManagerListFilter(emptyFilter);
    setAppliedFilter(emptyFilter);
    setCommonFilterModalOpen(false);
    await loadList(
      {
        pageNumber: 1,
        pageSize: paginationMeta.pageSize || DEFAULT_PAGE_SIZE,
      },
      emptyFilter
    );
  }, [buildBaselineEmptyListFilter, loadList, paginationMeta.pageSize, setAppliedFilter]);

  useEffect(() => {
    if (!commonResetFilter) return;
    const run = async () => {
      await handleFilterReset();
      setCommonResetFilter(false);
    };
    run();
  }, [commonResetFilter, handleFilterReset]);

  const handleFilterSubmit = async (formData = {}) => {
    const mergedFilter = mergeModalSubmitIntoListFilter(
      callManagerListFilter,
      callManagerFilterModalData,
      formData
    );
    setCallManagerListFilter(mergedFilter);
    setCommonFilterModalOpen(false);
    await loadList(
      {
        pageNumber: 1,
        pageSize: paginationMeta.pageSize || DEFAULT_PAGE_SIZE,
      },
      mergedFilter
    );
  };

  const handleToolbarTextFilterInputChange = (fieldKey, nextValue) => {
    setCallManagerListFilter((previousFilterState) => ({
      ...previousFilterState,
      [fieldKey]: nextValue ?? "",
    }));
  };

  const handleToolbarReactSelectFilterChange = (fieldKey, selectedOption) => {
    setCallManagerListFilter((previousFilterState) => ({
      ...previousFilterState,
      [fieldKey]: selectedOption?.value ?? "",
      [getAsyncFilterLabelFieldKey(fieldKey)]: selectedOption?.label ?? "",
    }));
  };

  const handleUpperToolbarFilterSearch = async () => {
    commitDraftAsApplied(callManagerListFilter);
    await loadList({
      pageNumber: 1,
      pageSize: paginationMeta.pageSize || DEFAULT_PAGE_SIZE,
    });
  };

  const handleCallDetails = (row) => {
    const candidateId = String(row?.id ?? "").trim();
    if (!candidateId) {
      toast.error("Candidate id is missing");
      return;
    }
    window.open(
      `/${urlEndPoint.callDetails}/${candidateId}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const handleVideoInterview = (row) => {
    const videoInterviewLink = String(row?.candidateVideoInterviewLink ?? "").trim();
    if (!videoInterviewLink) {
      toast.error("Video interview link is not available");
      return;
    }
    window.open(videoInterviewLink, "_blank", "noopener,noreferrer");
  };

  const handleMarkEligible = async (row) => {
    const recordId = String(row?.id ?? "").trim();
    if (!recordId) {
      toast.error("Candidate id is missing");
      return;
    }
    const result = await Swal.fire({
      title: "Mark as eligible",
      text: "Are you sure you want to mark this candidate as eligible?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, mark eligible",
      cancelButtonText: "Cancel",
    });
    if (!result.isConfirmed) return;

    setLoading(true);
    try {
      const response = await submitCallManagerOrderInProgressMarkEligible(recordId);
      if (response?.success || response?.isSuccess || response?.status) {
        toast.success(response?.message || "Candidate marked as eligible");
        await loadList({
          pageNumber: paginationMeta.pageNumber,
          pageSize: paginationMeta.pageSize || DEFAULT_PAGE_SIZE,
        });
        return;
      }
      toast.error(response?.message || "Unable to mark candidate as eligible");
    } finally {
      setLoading(false);
    }
  };

  const handleCallExecutiveQuickUpdateBlur = useCallback(
    async (rowRecord, nextValue, fieldAccessor) => {
      if (!isCallExecutiveFedexIdAccessor(fieldAccessor)) return;

      const callDetailsId = String(rowRecord?.id ?? "").trim();
      if (!callDetailsId) {
        toast.error("Candidate id is missing");
        return;
      }

      const previousValue = String(rowRecord?.[fieldAccessor] ?? "").trim();
      const nextTrimmed = String(nextValue ?? "").trim();
      if (previousValue === nextTrimmed) return;

      const response = await updateCallDetailsFedexId({
        callDetailsId,
        value: nextTrimmed,
      });
      if (response == null || response?.success === false) {
        if (response?.message) toast.error(response.message);
        return;
      }

      setTableData((previousRows) =>
        previousRows.map((row) =>
          String(row?.id ?? "").trim() === callDetailsId
            ? { ...row, [fieldAccessor]: nextTrimmed }
            : row
        )
      );

      if (response?.message) toast.success(response.message);
    },
    []
  );

  const handlePageChange = async (page) => {
    await loadList({
      pageNumber: page,
      pageSize: paginationMeta.pageSize || DEFAULT_PAGE_SIZE,
    });
  };

  const handleRowsPerPageChange = async (rows) => {
    await loadList({ pageNumber: 1, pageSize: rows });
  };

  const effectiveColumns =
    tableMetaData?.length > 0 ? tableMetaData : undefined;

  return (
    <React.Fragment>
      <div className="page-content call-exec-schedule-page">
        <Container fluid>
          <Card>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                <h5 className="fw-bold mb-0">Order in progress</h5>
              </div>
              <hr />

              {shouldRenderMetadataToolbarFilterRow ? (
                <MetadataFilterToolbar
                  toolbarDefinitions={metadataToolbarFilterDefinitions}
                  listFilter={callManagerListFilter}
                  onTextChange={handleToolbarTextFilterInputChange}
                  onSelectChange={handleToolbarReactSelectFilterChange}
                  onSearch={handleUpperToolbarFilterSearch}
                  onClear={handleFilterReset}
                  showClearWhenNoModalFilters={
                    !shouldShowTableHeaderFilterAndResetControls
                  }
                  toolbarDropdownOptionsByFieldName={
                    toolbarDropdownReactSelectOptionsByFieldName
                  }
                  layoutPreset="callExecutive"
                  pageName={PAGE_NAME}
                />
              ) : null}

              {loading ? (
                <Loaders />
              ) : effectiveColumns?.length ? (
                <TableContainer
                  columns={effectiveColumns}
                  data={tableData}
                  isActionRequired={hasAnyOrderInProgressRowAction}
                  isCheckboxLeftSide={false}
                  isPagination={true}
                  currentPage={paginationMeta.pageNumber}
                  rowsPerPage={paginationMeta.pageSize}
                  totalPages={paginationMeta.totalPages}
                  totalRecords={paginationMeta.totalRecords}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handleRowsPerPageChange}
                  setCommonFilterModalOpen={setCommonFilterModalOpen}
                  setCommonResetFilter={setCommonResetFilter}
                  allCommonFilter={shouldShowTableHeaderFilterAndResetControls}
                  activeFilterCount={activeModalFilterCount}
                  pageName={PAGE_NAME}
                  onCallExecutiveCall={
                    orderInProgressActionPermissions.canCallDetails
                      ? handleCallDetails
                      : undefined
                  }
                  onCallExecutiveVideoInterview={
                    orderInProgressActionPermissions.canVideoInterview
                      ? handleVideoInterview
                      : undefined
                  }
                  callExecutiveVideoInterviewRequiresLink
                  onCallExecutiveMarkEligible={
                    orderInProgressActionPermissions.canMarkEligible
                      ? handleMarkEligible
                      : undefined
                  }
                  onCallExecutiveQuickUpdateBlur={
                    orderInProgressActionPermissions.canEditFedexId
                      ? handleCallExecutiveQuickUpdateBlur
                      : undefined
                  }
                />
              ) : (
                <div className="text-muted text-center py-5">
                  Unable to load table columns.
                </div>
              )}
            </CardBody>
          </Card>

          <AllCommonFilterModal
            setCommonFilterModalOpen={setCommonFilterModalOpen}
            commonFilterModalOpen={commonFilterModalOpen}
            pageName={PAGE_NAME}
            modalTitle="Order in progress"
            handleSubmit={handleFilterSubmit}
            filterData={callManagerFilterModalData}
            metadataFilterDefinitions={metadataFiltersAll}
            enableFilterToolbarCustomization={usesMetadataDrivenFilterUi}
            commonResetFilter={commonResetFilter}
            setCommonResetFilter={setCommonResetFilter}
          />

        </Container>
      </div>
    </React.Fragment>
  );
};

OrderInProgress.propTypes = {
  orders: PropTypes.array,
  onGetOrders: PropTypes.func,
};

export default withRouter(OrderInProgress);
